// R-feat-2026-10-14 #5 易用性:顶部项目收藏选择器
//
// 设计:
// - 替换原 AppHeader 的 <Select> 简版项目下拉,保留所有 dropdown 行为
// - 下拉选项分两组:「收藏项目」(顶部,带★)、「其他可见项目」
// - 当前项目右侧★按钮:点击切换收藏(add/remove),乐观更新
// - 收藏失败/离线时 localStorage 兜底('pmwork-favorite-projects:<userId>')
// - 复用 useApiQuery + useApiMutation,queryKey 走 qk.userFavorites(userId)
//
// 集成点:AppHeader 中 showProjectSelector && accessibleProjects.length > 0 分支,
//        传入 props:projects / currentProjectId / onProjectChange / userId

import { memo, useCallback, useMemo } from 'react'
import { Select, Tooltip } from 'antd'
import { StarFilled, StarOutlined } from '@ant-design/icons'
import { useApiQuery } from '../../hooks/useApiQuery'
import { useApiMutation } from '../../hooks/useApiMutation'
import { useUserLocalStorage } from '../../hooks/useUserLocalStorage'
import { qk } from '../../constants/queryKeys'
import { useAuth } from '../../hooks/useAuth'
import { safeInt } from '../../utils'

const LS_KEY = 'pmwork-favorite-projects'

function ProjectFavoriteSelect({ projects, currentProjectId, onProjectChange, style }) {
  const { user } = useAuth()
  const userId = user?.id ?? null

  // 收藏列表(后端权威源)
  const { data: favs = [] } = useApiQuery(
    qk.userFavorites(userId),
    '/api/user/favorite-projects',
    undefined,
    // 未登录态不查
    { enabled: !!userId, staleTime: 60_000 },
  )
  const favoriteIds = useMemo(() => new Set(favs.map(f => f.project_id)), [favs])

  // localStorage 兜底(后端失败时 UI 仍能记号,刷新不丢)
  const [lsFavorites = [], setLsFavorites] = useUserLocalStorage(LS_KEY, [])
  const lsFavoriteIds = useMemo(() => new Set(lsFavorites.map(Number)), [lsFavorites])

  // toggle mutation:乐观更新 + invalidate
  const toggleMutation = useApiMutation(
    (payload) =>
      // 直接走 axios,useApiMutation 没有内置 axios 包装;走全局 instance 通过 fetch /api
      fetch('/api/user/favorite-projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).then(async (r) => {
        if (!r.ok) {
          const text = await r.text().catch(() => '')
          throw new Error(text || `HTTP ${r.status}`)
        }
        return r.json()
      }),
    {
      // 失败 / 成功 都触发 invalidate 由 React Query 兜底;LS 兜底在 onSuccess 后单独处理
      invalidateKeys: [qk.userFavorites(userId)],
    },
  )

  const handleToggle = useCallback(
    async (projectId, e) => {
      // 阻止下拉打开 / 选中
      if (e) {
        e.stopPropagation()
        e.preventDefault()
      }
      const isFav = favoriteIds.has(projectId) || lsFavoriteIds.has(projectId)
      const action = isFav ? 'remove' : 'add'
      // 先写 LS(乐观),失败不影响下次刷新
      const numId = Number(projectId)
      setLsFavorites((prev) => {
        const set = new Set(prev.map(Number))
        if (action === 'add') set.add(numId)
        else set.delete(numId)
        return Array.from(set)
      })
      try {
        await toggleMutation.mutateAsync({ project_id: projectId, action })
      } catch {
        // LS 已写,失败 UI 仍显示收藏状态
      }
    },
    [favoriteIds, lsFavoriteIds, setLsFavorites, toggleMutation],
  )

  // 下拉选项:收藏在顶,其他在后
  const options = useMemo(() => {
    const all = Array.isArray(projects) ? projects : []
    if (all.length === 0) return []
    // 合并后端 + LS 收藏 ID(LS 兜底)
    const mergedFavIds = new Set([...favoriteIds, ...lsFavoriteIds])
    const favProjects = all.filter(p => mergedFavIds.has(p.id))
    const otherProjects = all.filter(p => !mergedFavIds.has(p.id))
    const buildOption = (p) => {
      const isCurrent = safeInt(currentProjectId) === safeInt(p.id)
      return {
        value: p.id,
        // R-fix-2026-10-19: label 用纯 name(选中显示区),(当前)标记与★按钮
        // 改由 optionRender 渲染,只影响下拉项,不污染选中后的标签
        label: p.name,
        optionRender: () => (
          <OptionLabel
            name={p.name}
            isFav={mergedFavIds.has(p.id)}
            isCurrent={isCurrent}
            onToggle={(e) => handleToggle(p.id, e)}
            loading={toggleMutation.isPending}
          />
        ),
      }
    }
    const result = []
    if (favProjects.length > 0) {
      result.push({ label: '★ 收藏项目', options: favProjects.map(buildOption) })
    }
    if (otherProjects.length > 0) {
      result.push({ label: '其他项目', options: otherProjects.map(buildOption) })
    }
    return result
  }, [projects, favoriteIds, lsFavoriteIds, currentProjectId, handleToggle, toggleMutation.isPending])

  return (
    <Select
      value={currentProjectId ? safeInt(currentProjectId) : undefined}
      onChange={onProjectChange}
      style={style || { width: 280 }}
      placeholder="选择项目"
      options={options}
      // 选项 label 是 ReactNode,搜索匹配按 name;antd 默认按 value/label 字符串过滤,
      // label 是 ReactNode 时不报错,只是输入字符无法过滤 — 这里不实现搜索过滤,
      // dropdown 已分组,目测够用。R-feat 待办:加 server-side search。
      optionFilterProp={null}
      showSearch={false}
    />
  )
}

// 单条选项 label:项目名 + ★按钮
const OptionLabel = memo(function OptionLabel({ name, isFav, isCurrent, onToggle, loading }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {name}
        {isCurrent && <span style={{ marginLeft: 6, color: 'var(--brand-navy)', fontSize: 12 }}>(当前)</span>}
      </span>
      <Tooltip title={isFav ? '取消收藏' : '收藏到顶部'} mouseEnterDelay={0.3}>
        <span
          role="button"
          aria-label={isFav ? '取消收藏' : '收藏项目'}
          tabIndex={0}
          onClick={onToggle}
          // 不触发 Select 的 option click
          onMouseDown={(e) => { e.stopPropagation(); e.preventDefault() }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 22,
            height: 22,
            cursor: loading ? 'wait' : 'pointer',
            color: isFav ? '#faad14' : 'var(--text-secondary, #888)',
            fontSize: 14,
            opacity: loading ? 0.6 : 1,
          }}
        >
          {isFav ? <StarFilled /> : <StarOutlined />}
        </span>
      </Tooltip>
    </div>
  )
})

export default memo(ProjectFavoriteSelect)
